import axios from 'axios'
import { useQuery } from '@tanstack/react-query'
import { dashboardsService } from '@/services/dashboards'
import type {
  DashboardBoard,
  DashboardPanelData,
  DashboardPayload,
} from '@/types/dashboards'

/** A board is a daily briefing left open on a second monitor — it refreshes
 *  on its own rather than waiting for a reload. */
export const DASHBOARD_REFETCH_MS = 30_000

export function dashboardQueryKey(board: DashboardBoard) {
  return ['dashboards', board] as const
}

/**
 * Reads one panel out of a board document.
 *
 * Returns null rather than throwing for an absent key: a board that ships a
 * panel before its endpoint does should render an empty state, not a crash.
 */
export function getPanel<T extends DashboardPanelData>(
  panels: Record<string, DashboardPanelData>,
  key: string,
): T | null {
  const panel = panels[key]
  return panel ? (panel as T) : null
}

export function useDashboard(board: DashboardBoard) {
  const query = useQuery<DashboardPayload>({
    queryKey: dashboardQueryKey(board),
    queryFn: async ({ signal }) => {
      try {
        return await dashboardsService.getBoard(board, signal)
      } catch (error) {
        // Axios often reports an aborted poll as "Network Error". React Query
        // only swallows AbortError, so without this remap a superseded 30s
        // refresh paints every panel as a failed load.
        if (
          signal.aborted ||
          (axios.isAxiosError(error) && error.code === 'ERR_CANCELED')
        ) {
          throw new DOMException('The board request was aborted.', 'AbortError')
        }
        throw error
      }
    },
    refetchInterval: DASHBOARD_REFETCH_MS,
    refetchOnWindowFocus: true,
  })

  return {
    payload: query.data ?? null,
    panels: query.data?.panels ?? {},
    generatedAt: query.data?.generatedAt ?? null,
    partition: query.data?.partition ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    /** Drives the Live chip and the last-updated stamp in DashboardShell. */
    dataUpdatedAt: query.dataUpdatedAt,
  }
}
